import { isTag } from 'anodum';
import textFormatting from '../../text-formatting';
import { LIST_ITEM } from '../constants/editable-elements';
import isEmptyContentEditable from '../verifiers/is-empty-content-editable';
import removeSelection from './remove-selection';
import removeEmptyListItemsAfterRemoveSelection from './remove-empty-list-items-after-remove-selection';
import prepareStructure from './prepare-structure';
import handleEmptyContentEditableSelection from './handle-empty-content-editable-selection';
import createClonedSelectionData from './create-cloned-selection-data';
import handleExternalImageSources from './handle-external-image-sources';
import restoreCollapsedSelection from './restore-collapsed-selection';
import parseFragmentFromString from './parse-fragment-from-string';
import insertClipboardData from './clipboard-insertion/insert-clipboard-data';
import prepareClipboardData from './clipboard-insertion/prepare-clipboard-data';

const applyInsertion = async (element, textHtmlData) => {
  const contentEditable = element;
  const { ownerDocument: liveDocument } = element;

  /**
   * Prepare clipboard data
   * Cut off everything outside the `<body>` tag
   * and keep just content of body
   */

  const data = prepareClipboardData(textHtmlData);

  const pasteFragment = liveDocument.importNode(parseFragmentFromString(data), true);

  /**
   * Zero phase.
   * Gathering all image sources that we recognise as external, saving them to our system
   * and replacing sources to new one.
   */

  await handleExternalImageSources(pasteFragment);

  const selection = textFormatting.accessors.getAdvancedSelection();
  const range = selection.getRangeAt(0);
  const listItems = range.getNodes([Node.ELEMENT_NODE], (el) => isTag(el, LIST_ITEM));

  /**
   * Zero phase.
   * Removing selection and if it necessary empty unneeded elements.
   */

  removeSelection();
  removeEmptyListItemsAfterRemoveSelection(listItems);

  /**
   * First phase.
   * Here we make first iteration of making clipboard acceptable to the platform.
   */

  prepareStructure(pasteFragment);

  /**
   * First phase.
   * Prepare html-grid of content editable in case it empty based on clipboard.
   * It necessary for the next phases.
   */

  if (isEmptyContentEditable(contentEditable)) {
    handleEmptyContentEditableSelection(contentEditable, pasteFragment);
  }

  const { clonedSelectionData, clonedContentEditable } = createClonedSelectionData(contentEditable);
  if (!pasteFragment.childNodes.length) return;

  /**
   * Second phase.
   * Insertion stage. There are algorithm works with selection data
   * and makes a special actions (appending, unwrapping, removing) dependent on
   * selection place and clipboard.
   */

  const newCollapsedSelectionData = insertClipboardData(pasteFragment, clonedSelectionData);

  /**
   * Third phase.
   * Second iteration of preparing grid. It is necessary to fix
   * newly created incompatibilities with platform after insertion.
   * (e.g. nested lists)
   *
   *
   * TODO: optimize this if it will be necessary
   * Note: ATM, on this phase we process whole content editable element (e.g. ws-text[contenteditable])
   * and replace it each pasting time. Sometimes it could make an influence to the performance.
   */
  prepareStructure(clonedContentEditable);

  contentEditable.innerHTML = '';
  textFormatting.helpers.addRelatedClassesToElement(clonedContentEditable);
  const fragment = liveDocument.createDocumentFragment();
  fragment.append(...clonedContentEditable.childNodes);
  contentEditable.append(fragment);
  // *** //

  restoreCollapsedSelection(newCollapsedSelectionData);
};

export default applyInsertion;
